import React, { useState, useEffect } from 'react';
import { Users, Send, Download, AlertTriangle, Database, Settings, ArrowUpRight, TrendingUp, X, ShieldCheck } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import adminService from '../../services/adminService';

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [systemHealth, setSystemHealth] = useState(null);
    const [config, setConfig] = useState({ widgets: ['stats', 'charts', 'logs', 'stress'], refreshInterval: 30 });
    const [loading, setLoading] = useState(true);
    const [isConfigOpen, setIsConfigOpen] = useState(false);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const [sData, hData] = await Promise.all([
                    adminService.getStats(),
                    adminService.getSystemHealth()
                ]);
                setStats(sData);
                setSystemHealth(hData);
            } catch (error) {
                console.error('Failed to refresh data', error);
            }
        };

        const fetchData = async () => {
            try {
                const [statsData, configData, healthData] = await Promise.all([
                    adminService.getStats(),
                    adminService.getDashboardConfig(),
                    adminService.getSystemHealth()
                ]);
                setStats(statsData);
                setSystemHealth(healthData);
                if (configData) setConfig(configData);
            } catch (error) {
                console.error('Failed to fetch dashboard data', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        const interval = setInterval(fetchStats, config.refreshInterval * 1000);
        return () => clearInterval(interval);
    }, [config.refreshInterval]);

    const updateConfig = async (newConfig) => {
        try {
            await adminService.updateDashboardConfig(newConfig);
            setConfig(newConfig);
        } catch (error) {
            console.error('Config update failed');
        }
    };

    const statCards = [
        { title: 'Total User Accounts', value: stats?.totalUsers || 0, icon: <Users size={24} />, color: 'blue', subtext: 'Profile Completion: 94%', trend: '+2' },
        { title: 'Messages Sent', value: stats?.emailsSentToday || 0, icon: <Send size={24} />, color: 'emerald', subtext: 'Avg. Speed: 420ms', trend: '+5.4%' },
        { title: 'Messages Received', value: stats?.emailsReceivedToday || 0, icon: <Download size={24} />, color: 'blue', subtext: 'Queue: 0', trend: 'STABLE' },
        { title: 'Failed Messages', value: stats?.failedEmails || 0, icon: <AlertTriangle size={24} />, color: 'red', subtext: 'Timeouts: 5', trend: 'RETRY' },
        { title: 'Disk Space Used', value: `${stats?.storageUsedMB || 0} MB`, icon: <Database size={24} />, color: 'amber', subtext: 'Storage Policy: 90 Days', trend: `${(((stats?.storageUsedMB || 0) / ((stats?.totalUsers || 1) * 100 + 1000)) * 100).toFixed(1)}%` },
    ];

    if (loading) return (
        <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
            <div className="w-12 h-12 border-4 border-[#4169E1]/20 border-t-[#4169E1] rounded-full animate-spin"></div>
            <p className="text-slate-500 font-bold tracking-widest uppercase text-sm">Loading Dashboard...</p>
        </div>
    );

    return (
        <div className="space-y-8 pb-10">
            <header className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-extrabold tracking-tight underline decoration-[#4169E1]/30">Overview Dashboard</h1>
                    <p className="text-slate-500 mt-1 font-medium">Real-time system performance and health stats.</p>
                </div>
                <div className="flex items-center space-x-3">
                    <div className="hidden lg:flex items-center px-4 py-2 rounded-xl bg-[#4169E1]/5 border border-[#4169E1]/10 text-xs font-bold text-[#4169E1] uppercase tracking-widest animate-pulse">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#4169E1] mr-2"></div>
                        System Online
                    </div>
                    <button
                        onClick={() => setIsConfigOpen(!isConfigOpen)}
                        className="p-3 enterprise-card bg-[rgb(var(--card))] hover:scale-105 active:scale-95 text-slate-500"
                    >
                        <Settings size={20} />
                    </button>
                    {isConfigOpen && (
                        <div className="absolute top-44 right-14 z-30 bg-[rgb(var(--card))] glass p-8 rounded-3xl border border-[rgb(var(--border))] shadow-2xl w-96 space-y-6 animate-fade-in">
                            <div className="flex justify-between items-center border-b border-[rgb(var(--border))] pb-4">
                                <h3 className="font-extrabold text-lg">System Layout</h3>
                                <button onClick={() => setIsConfigOpen(false)}><X size={20} /></button>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest block mb-3">Refresh Speed</label>
                                    <select
                                        className="premium-input"
                                        value={config.refreshInterval}
                                        onChange={(e) => updateConfig({ ...config, refreshInterval: parseInt(e.target.value) })}
                                    >
                                        <option value={10}>Fast (10s)</option>
                                        <option value={30}>Balanced (30s)</option>
                                        <option value={60}>Eco-Mode (60s)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest block mb-3">Dashboard Modules</label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {['stats', 'charts', 'logs', 'stress'].map(widget => (
                                            <button
                                                key={widget}
                                                onClick={() => {
                                                    const newWidgets = config.widgets.includes(widget)
                                                        ? config.widgets.filter(w => w !== widget)
                                                        : [...config.widgets, widget];
                                                    updateConfig({ ...config, widgets: newWidgets });
                                                }}
                                                className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all border ${config.widgets.includes(widget)
                                                    ? 'bg-[#4169E1] text-white border-[#4169E1]'
                                                    : 'bg-transparent border-[rgb(var(--border))] text-slate-400'
                                                    }`}
                                            >
                                                {widget}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </header>

            {config.widgets.includes('stats') && (
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
                    {statCards.map((card, i) => (
                        <div key={i} className="enterprise-card p-6 group hover:-translate-y-1">
                            <div className="flex justify-between items-start mb-4">
                                <div className={`p-4 rounded-2xl group-hover:scale-110 transition-transform ${card.color === 'blue' ? 'bg-[#4169E1]/10 text-[#4169E1]' :
                                    card.color === 'emerald' ? 'bg-emerald-500/10 text-emerald-500' :
                                        card.color === 'red' ? 'bg-red-500/10 text-red-500' :
                                            'bg-amber-500/10 text-amber-500'
                                    }`}>
                                    {card.icon}
                                </div>
                                <div className={`flex items-center text-xs font-bold px-2 py-0.5 rounded-lg ${card.trend.startsWith('+') ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'
                                    }`}>
                                    {card.trend}
                                </div>
                            </div>
                            <div className="space-y-1">
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{card.title}</p>
                                <p className="text-3xl font-extrabold tracking-tight">{card.value}</p>
                                <p className="text-xs font-medium text-slate-400 italic pt-1">{card.subtext}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {config.widgets.includes('charts') && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 enterprise-card p-8">
                        <div className="flex justify-between items-center mb-8">
                            <div>
                                <h3 className="text-xl font-bold tracking-tight">Message Traffic</h3>
                                <p className="text-sm text-slate-400 font-medium">Detailed view of sent and received emails.</p>
                            </div>
                            <div className="flex items-center space-x-3">
                                <select className="bg-slate-50 dark:bg-slate-900 border border-[rgb(var(--border))] rounded-lg text-xs font-bold uppercase tracking-widest px-3 py-1.5 outline-none focus:ring-2 ring-[#4169E1]/20">
                                    <option>Last 24 Hours</option>
                                    <option>Last 7 Days</option>
                                    <option>Last 30 Days</option>
                                </select>
                                <span className="flex items-center text-xs font-bold text-slate-400">
                                    <span className="w-2 h-2 bg-[#4169E1] rounded-full mr-2"></span>
                                    Message Flow
                                </span>
                            </div>
                        </div>
                        <div className="h-[350px] min-h-[350px] w-full relative" style={{ minWidth: 0, minHeight: 350 }}>
                            <ResponsiveContainer width="100%" height={350} minWidth={0} minHeight={350}>
                                <AreaChart data={stats?.emailTrend?.map(item => ({ date: item[0], count: item[1] })) || []}>
                                    <defs>
                                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#4169E1" stopOpacity={0.1} />
                                            <stop offset="95%" stopColor="#4169E1" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(var(--foreground), 0.05)" />
                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b', fontWeight: 600 }} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b', fontWeight: 600 }} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'rgba(var(--card), 0.9)',
                                            borderRadius: '16px',
                                            border: '1px solid rgba(var(--border), 1)',
                                            backdropFilter: 'blur(10px)',
                                            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                                        }}
                                        itemStyle={{ color: '#4169E1', fontWeight: 800 }}
                                    />
                                    <Area type="monotone" dataKey="count" stroke="#4169E1" strokeWidth={4} fillOpacity={1} fill="url(#colorCount)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="enterprise-card p-8 flex flex-col">
                        <h3 className="text-xl font-bold tracking-tight mb-2">Delivery Success Rate</h3>
                        <p className="text-sm text-slate-400 font-medium mb-8">Percentage of messages delivered successfully.</p>

                        <div className="flex-1 flex flex-col justify-center space-y-8">
                            <div className="relative flex justify-center items-center">
                                <div className="absolute flex flex-col items-center">
                                    <span className="text-4xl font-extrabold tracking-tighter">
                                        {stats?.successVsFailure && stats.successVsFailure.length >= 2 ?
                                            (((stats.successVsFailure[0].value) / (Math.max(1, stats.successVsFailure[0].value + stats.successVsFailure[1].value))) * 100).toFixed(1) + '%'
                                            : '0%'}
                                    </span>
                                    <span className="text-xs font-bold text-emerald-500 uppercase tracking-widest">{stats ? 'Live Pipeline' : 'Calibrating'}</span>
                                </div>
                                <div className="w-48 h-48 rounded-full border-[12px] border-slate-100 dark:border-slate-800 relative">
                                    <div className="absolute -top-3 -bottom-3 -left-3 -right-3 rounded-full border-[12px] border-emerald-500 border-t-transparent animate-spin-slow" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 80%, 0 80%)' }}></div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex justify-between items-center p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/50">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                                        <span className="text-xs font-bold uppercase tracking-wider">Successful Messages</span>
                                    </div>
                                    <span className="text-sm font-extrabold">{stats?.successVsFailure?.[0]?.value?.toLocaleString() || 0}</span>
                                </div>
                                <div className="flex justify-between items-center p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/50">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                        <span className="text-xs font-bold uppercase tracking-wider">Failed Messages</span>
                                    </div>
                                    <span className="text-sm font-extrabold text-red-500">{stats?.successVsFailure?.[1]?.value?.toLocaleString() || 0}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {config.widgets.includes('stress') && (
                <div className="enterprise-card p-8 group">
                    <div className="flex justify-between items-center mb-8">
                        <div>
                            <h3 className="text-xl font-bold tracking-tight">System Load & Performance</h3>
                            <p className="text-sm text-slate-400 font-medium">Monitor CPU, memory, and message queue performance.</p>
                        </div>
                        <div className={`px-4 py-1.5 rounded-xl text-xs font-extrabold uppercase tracking-widest border transition-all ${systemHealth?.pressureStatus === 'CRITICAL' ? 'bg-red-500 text-white border-red-500 shadow-lg shadow-red-500/30 animate-pulse' :
                            systemHealth?.pressureStatus === 'ELEVATED' ? 'bg-amber-500 text-white border-amber-500' :
                                'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                            }`}>
                            Status: {systemHealth?.pressureStatus || 'NOMINAL'}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="space-y-3">
                            <div className="flex justify-between text-xs font-bold text-slate-400 uppercase tracking-tighter px-1">
                                <span>Compute Load</span>
                                <span>{systemHealth?.cpuLoad || '0%'}</span>
                            </div>
                            <div className="h-2 bg-slate-100 dark:bg-slate-900 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-[#4169E1] transition-all duration-1000 ease-out"
                                    style={{ width: systemHealth?.cpuLoad || '0%' }}
                                ></div>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div className="flex justify-between text-xs font-bold text-slate-400 uppercase tracking-tighter px-1">
                                <span>Memory Commitment</span>
                                <span>{systemHealth?.memoryUsage || '0%'}</span>
                            </div>
                            <div className="h-2 bg-slate-100 dark:bg-slate-900 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-purple-500 transition-all duration-1000 ease-out"
                                    style={{ width: systemHealth?.memoryUsage || '0%' }}
                                ></div>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div className="flex justify-between text-xs font-bold text-slate-400 uppercase tracking-tighter px-1">
                                <span>Message Queue</span>
                                <span>{systemHealth?.queueDepth || 0} items</span>
                            </div>
                            <div className="h-2 bg-slate-100 dark:bg-slate-900 rounded-full overflow-hidden">
                                <div
                                    className={`h-full transition-all duration-1000 ease-out ${(systemHealth?.queueDepth || 0) > 20 ? 'bg-red-500' : 'bg-emerald-500'
                                        }`}
                                    style={{ width: `${Math.min((systemHealth?.queueDepth || 0) * 5, 100)}%` }}
                                ></div>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">System Response Time</p>
                            <p className="text-2xl font-black tracking-tight text-[#4169E1]">{systemHealth?.p95Latency || '240ms'}</p>
                            <div className="flex items-center text-[10px] font-extrabold text-slate-400 uppercase tracking-tighter">
                                <div className="w-1 h-1 rounded-full bg-[#4169E1] mr-1.5 animate-ping"></div>
                                {systemHealth?.activeConnections || 0} Active Connections
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {config.widgets.includes('logs') && (
                <div className="enterprise-card p-8">
                    <div className="flex justify-between items-center mb-8">
                        <div>
                            <h3 className="text-xl font-bold tracking-tight">Activity Logs</h3>
                            <p className="text-sm text-slate-400 font-medium">A record of all important system actions and changes.</p>
                        </div>
                        <button className="premium-button-secondary py-2 px-6 flex items-center text-xs uppercase font-bold tracking-widest">
                            Export Logs
                            <ArrowUpRight size={14} className="ml-2" />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(stats?.recentLogs || []).length > 0 ? (stats.recentLogs.map((log, i) => (
                            <div key={i} className="flex items-center space-x-5 p-5 border border-[rgb(var(--border))] rounded-2xl hover:bg-slate-50 dark:hover:bg-[#4169E1]/10 transition-colors group">
                                <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center text-slate-400 group-hover:text-[#4169E1] group-hover:bg-[#4169E1]/10 transition-all">
                                    <TrendingUp size={20} />
                                </div>
                                <div className="flex-1">
                                    <div className="flex justify-between items-center mb-1">
                                        <p className="text-sm font-extrabold tracking-tight italic text-[#4169E1] dark:text-[#E0E7FF] uppercase text-xs font-mono">#{Math.random().toString(36).substring(7).toUpperCase()}</p>
                                        <span className="text-xs font-bold text-slate-400 font-mono italic">{new Date(log.timestamp).toLocaleTimeString()} · AD-CLUSTER</span>
                                    </div>
                                    <p className="text-xs text-slate-700 dark:text-slate-300 font-bold line-clamp-1">{log.action}: {log.details}</p>
                                </div>
                            </div>
                        ))) : (
                            <div className="col-span-2 text-center py-20 bg-slate-50 dark:bg-slate-900/30 rounded-3xl border border-dashed border-slate-200 dark:border-slate-800">
                                <ShieldCheck size={48} className="mx-auto text-slate-200 dark:text-slate-800 mb-4" />
                                <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">No Log Entries Found</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <footer className="mt-12 flex justify-between items-center px-4 py-6 border-t border-[rgb(var(--border))] opacity-60">
                <div className="flex items-center space-x-4 text-xs font-bold text-slate-400 uppercase tracking-[0.2em]">
                    <span>Control Node: Alpha-01</span>
                    <span className="w-1 h-1 bg-slate-400 rounded-full"></span>
                    <span>Region: Global-B</span>
                </div>
                <div className="flex items-center space-x-4 text-xs font-bold text-slate-400 uppercase tracking-[0.2em]">
                    <span>Next Sync In: {config.refreshInterval}s</span>
                    <span className="w-1 h-1 bg-slate-400 rounded-full"></span>
                    <span>System Status: <span className="text-emerald-500">Nominal</span></span>
                </div>
            </footer>
        </div>
    );
};

export default AdminDashboard;
